import { useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import {
  OrgReportDocument,
  ReportStatus,
  useDeleteOrgReportMutation,
  useGenerateOrgReportMutation,
  useOrgReportsQuery,
  useRegenerateOrgReportMutation,
  type OrgReportFullFragment,
  type OrgReportQuery,
  type OrgReportQueryVariables,
} from "@/graphql/generated";
import { REPORTS_PAGE_SIZE } from "../constants";

/** How often to poll while any report is still pending/generating. */
const POLL_INTERVAL_MS = 5000;

/**
 * Fetches the most recent reports for an org and quietly polls while any
 * row is still in flight. Once every row is terminal (COMPLETE or FAILED),
 * polling stops automatically — no wasted bandwidth. Accepts a nullable
 * org id so callers don't need their own guard against the org-picker race.
 *
 * Pagination uses a growing window: callers bump `limit` to load more.
 * This keeps the polling refetch covering every loaded row (simpler than
 * skip-based fetchMore + cache merge).
 */
export function useOrgReports(organizationId: string | null, limit: number = REPORTS_PAGE_SIZE) {
  const result = useOrgReportsQuery({
    variables: { organizationId: organizationId ?? "", limit, skip: 0 },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
    pollInterval: 0, // set below based on data
    notifyOnNetworkStatusChange: true,
  });

  const inFlight = useMemo(
    () =>
      (result.data?.orgReports ?? []).some(
        (r) =>
          r.status === ReportStatus.Pending ||
          r.status === ReportStatus.Generating,
      ),
    [result.data?.orgReports],
  );

  // Adjust pollInterval after each fetch — Apollo lets us set it via the
  // returned `startPolling`/`stopPolling`, but it also picks up the latest
  // `pollInterval` from re-render-time options. Re-render trigger: the
  // `inFlight` value.
  if (inFlight) {
    result.startPolling(POLL_INTERVAL_MS);
  } else {
    result.stopPolling();
  }

  return result;
}

/**
 * One-shot generate mutation. Re-fetches the OrgReports list on success so
 * the newly-PENDING row appears on top immediately. `organizationId` is
 * unused here — callers build the full input — but kept on the signature
 * for parity with the other hooks and forward-compat (cache.modify).
 */
export function useGenerateOrgReport(organizationId: string | null) {
  // Reference the parameter explicitly so noUnusedParameters doesn't flag
  // the future-friendly signature.
  void organizationId;
  return useGenerateOrgReportMutation({
    refetchQueries: ["OrgReports"],
    awaitRefetchQueries: true,
  });
}

/**
 * Re-run an existing report. Backend deletes the old PDF from S3, transitions
 * the row to PENDING, and re-enqueues. Same OrgReport row is reused, so the
 * list doesn't grow on each retry/regenerate.
 */
export function useRegenerateOrgReport() {
  return useRegenerateOrgReportMutation({
    refetchQueries: ["OrgReports"],
    awaitRefetchQueries: true,
  });
}

/**
 * Soft-delete a report. Backend purges the PDF from S3 and marks the row
 * with deletedAt/deletedBy; subsequent OrgReports queries exclude it.
 */
export function useDeleteOrgReport() {
  return useDeleteOrgReportMutation({
    refetchQueries: ["OrgReports"],
    awaitRefetchQueries: true,
  });
}

/**
 * Force-fresh fetch of a single report (no cache) and resolve the
 * presigned downloadUrl. Triggers the browser download via window.open;
 * the URL is **never** stored client-side — re-fetching is the contract.
 */
export function useDownloadReport() {
  const apollo = useApolloClient();

  return async (reportId: string): Promise<OrgReportFullFragment | null> => {
    const res = await apollo.query<OrgReportQuery, OrgReportQueryVariables>({
      query: OrgReportDocument,
      variables: { id: reportId },
      fetchPolicy: "network-only",
    });
    const url = res.data?.orgReport?.downloadUrl;
    if (!url) return null;
    window.open(url, "_blank", "noopener,noreferrer");
    return res.data?.orgReport ?? null;
  };
}
